import { GroupsOutlined, RemoveCircleOutline, Search, FilterAltOff } from '@mui/icons-material'
import {
    Box,
    Chip,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { ReactElement, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { HttpUser } from 'src/http/user/httpUser'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import {
    ItecGridColsDef as GridColsDef,
    ItecColFilters,
    ItecDataGrid,
    useItecDataGrid
} from 'src/lib/itaptec/datagrid/src'
import { EntityWithIdFields, EntityWithIdAndDescriptionFields, PaisLogoFields, schemaSelectionOption } from 'src/types'
import {
    UserDTOFields,
    UserGroupFilterDTOFields,
    UserGroupViewDTO,
    UserGroupViewDTOFields,
    UserViewDTO
} from 'src/types/user/userData'
import { z } from 'zod'
import { CountryAvatar } from '../../../../../../components/icons/CountryAvatar'
import { Controller } from 'react-hook-form'

export type EditUserGroupsProps = {
    user: UserViewDTO
    onClose: () => void
}

const schemaFiltro = z.object({
    [UserGroupFilterDTOFields.GroupName]: z.string().optional(),
    [UserGroupFilterDTOFields.CodsSistemaPrincipal]: z.array(schemaSelectionOption).optional(),
    [UserGroupFilterDTOFields.CodsMensajeTipo]: z.array(schemaSelectionOption).optional(),
    [UserGroupFilterDTOFields.SoloActivos]: z.array(schemaSelectionOption).optional()
})

type SchemaFiltro = z.infer<typeof schemaFiltro>

const opcionActivo = { value: 1, label: 'Activos', color: 'success' }
const opcionTodos = { value: 2, label: 'Todos', color: 'info' }

const toggleActivoOptions: any[] = [opcionActivo, opcionTodos]

function EditUserGroups({ user, onClose }: EditUserGroupsProps) {
    const { paisLogos } = usePaisLogo()
    const [manualSelection, setManualSelection] = useState<number[]>()

    const opcionSoloActivos = (filter: SchemaFiltro) => {
        const fieldValue = filter?.[UserGroupFilterDTOFields.SoloActivos]
        if (Array.isArray(fieldValue) && fieldValue.length === 1) {
            if (fieldValue[0].value === opcionActivo.value) return true
            if (fieldValue[0].value === opcionTodos.value) return undefined
        }
        return undefined
    }

    const columnas: GridColsDef<UserGroupViewDTO>[] = [
        {
            headerName: 'Grupo',
            field: UserGroupViewDTOFields.GroupName,
            flex: 3
        },
        {
            headerName: 'País',
            field: UserGroupViewDTOFields.CountryName,
            flex: 2,
            renderCell: ({ row: group }) => (
                <Chip
                    label={group[UserGroupViewDTOFields.CountryName]}
                    size='small'
                    variant='outlined'
                    avatar={<CountryAvatar countryCode={group[UserGroupViewDTOFields.CountryCode]} />}
                />
            )
        },
        {
            headerName: 'Circuito',
            field: UserGroupViewDTOFields.NombreSistema,
            flex: 3
        },
        {
            headerName: 'Activo',
            field: UserGroupViewDTOFields.GroupAssigned,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            type: 'actions',
            width: 60,
            getActions: ({ row: group }) => {
                const groupId = group[UserGroupViewDTOFields.GroupId]
                const isAssigned = manualSelection?.includes(groupId)

                if (!isAssigned) return []

                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title='Quitar del grupo'>
                                <RemoveCircleOutline fontSize='small' />
                            </Tooltip>
                        }
                        label='Quitar'
                        onClick={() => setManualSelection(prev => (prev ?? []).filter(id => id !== groupId))}
                        color='error'
                    />
                ]
            }
        }
    ]

    // Filtros internos para la reactividad del query (no se renderizan en headers)
    const filtros: ItecColFilters<SchemaFiltro> = {
        [UserGroupViewDTOFields.GroupName]: {
            type: 'text',
            name: UserGroupFilterDTOFields.GroupName,
            props: { debounceTime: 500, size: 'small' }
        },
        [UserGroupViewDTOFields.CountryName]: {
            type: 'multiselect',
            name: UserGroupFilterDTOFields.CodsSistemaPrincipal,
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        },
        [UserGroupViewDTOFields.NombreSistema]: {
            type: 'multiselect',
            name: UserGroupFilterDTOFields.CodsMensajeTipo,
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        },
        [UserGroupViewDTOFields.GroupAssigned]: {
            type: 'multiple-button-group',
            name: UserGroupFilterDTOFields.SoloActivos,
            props: { options: toggleActivoOptions, enforceValueSet: false, size: 'small' }
        }
    }

    const {
        datagridProps,
        control: {
            query: { data },
            form: filterForm
        }
    } = useItecDataGrid<UserGroupViewDTO, SchemaFiltro>({
        pagination: false,
        columns: columnas,
        filters: filtros,
        checkboxSelection: true,
        getRowId: row => row[UserGroupViewDTOFields.GroupId],
        getEntities: (filter) =>
            HttpUser.getGroupsByUser(user[EntityWithIdFields.Id], filter ? {
                [UserGroupFilterDTOFields.GroupName]: filter[UserGroupFilterDTOFields.GroupName],
                [UserGroupFilterDTOFields.CodsSistemaPrincipal]: filter[UserGroupFilterDTOFields.CodsSistemaPrincipal]?.map(c => (c as any).id ?? c.value),
                [UserGroupFilterDTOFields.CodsMensajeTipo]: filter[UserGroupFilterDTOFields.CodsMensajeTipo]?.map(c => (c as any).id ?? c.value),
                [UserGroupFilterDTOFields.SoloActivos]: opcionSoloActivos(filter as SchemaFiltro)
            } : {})
    })

    // Default: mostrar solo activos
    useEffect(() => {
        if (filterForm) {
            filterForm.setValue(UserGroupFilterDTOFields.SoloActivos, [opcionActivo] as any)
        }
    }, [])

    // Sync initial selection from server data
    useEffect(() => {
        if (data && !manualSelection) {
            const groups = data as unknown as UserGroupViewDTO[]
            const assigned = groups
                .filter(g => g[UserGroupViewDTOFields.GroupAssigned])
                .map(g => g[UserGroupViewDTOFields.GroupId])
            setManualSelection(assigned)
        }
    }, [data])

    // Sobreescribir columnas para quitar menus de filtro de los headers
    const cleanDatagridProps = {
        ...datagridProps,
        columns: datagridProps.columns.map(col => ({
            ...col,
            disableColumnMenu: true,
            renderHeader: undefined
        })),
        slots: undefined,
        getRowId: (row: UserGroupViewDTO) => row[UserGroupViewDTOFields.GroupId],
        rowSelectionModel: manualSelection ?? [],
        onRowSelectionModelChange: (model: any) => setManualSelection(model as number[]),
        keepNonExistentRowsSelected: true,
        density: 'compact' as const
    }

    const handleClearFilters = () => {
        if (!filterForm) return
        filterForm.reset({
            [UserGroupFilterDTOFields.GroupName]: undefined,
            [UserGroupFilterDTOFields.CodsSistemaPrincipal]: undefined,
            [UserGroupFilterDTOFields.CodsMensajeTipo]: undefined,
            [UserGroupFilterDTOFields.SoloActivos]: [opcionActivo] as any
        })
    }

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudieron guardar los grupos'

        const promise = HttpUser.updateUserGroups(user[EntityWithIdFields.Id], manualSelection || [])
            .then(() => {
                setTimeout(onClose, 500)
            })
            .catch(err => {
                mensajeError = err?.message || mensajeError
                throw err
            })

        toast.promise(promise, {
            success: 'Grupos actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    const userName = `${user[UserDTOFields.FirstName] ?? ''} ${user[UserDTOFields.LastName] ?? ''}`.trim()

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <GroupsOutlined color='primary' />
                    <Typography variant='h6' fontWeight={600}>
                        Grupos de {userName || 'usuario'}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Barra de filtros */}
                {filterForm && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'action.hover'
                        }}
                    >
                        <Controller
                            control={filterForm.control}
                            name={UserGroupFilterDTOFields.GroupName}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value ?? ''}
                                    label='Grupo'
                                    size='small'
                                    sx={{ minWidth: 180, flex: 1 }}
                                    InputProps={{
                                        startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                                    }}
                                />
                            )}
                        />
                        <Box sx={{ minWidth: 180, flex: 1 }}>
                            <ControlledAutocomplete
                                control={filterForm.control}
                                name={UserGroupFilterDTOFields.CodsSistemaPrincipal}
                                label='País'
                                                                loadOptions={() =>
                                    Promise.resolve(paisLogos).then(res =>
                                        res.map(s => ({
                                            id: s.id,
                                            descripcion: s[EntityWithIdAndDescriptionFields.Description]
                                        }))
                                    )
                                }
                                multiple
                            />
                        </Box>
                        <Box sx={{ minWidth: 180, flex: 1 }}>
                            <ControlledAutocomplete
                                control={filterForm.control}
                                name={UserGroupFilterDTOFields.CodsMensajeTipo}
                                label='Circuito'
                                                                loadOptions={() =>
                                    HttpCacheWorkflow.getSistemasTipos().then(res =>
                                        res.map(s => ({
                                            id: s.id,
                                            descripcion: s.descripcion
                                        }))
                                    )
                                }
                                multiple
                            />
                        </Box>
                        <Controller
                            control={filterForm.control}
                            name={UserGroupFilterDTOFields.SoloActivos}
                            render={({ field }) => (
                                <ToggleButtonGroup
                                    size='small'
                                    exclusive
                                    value={(field.value ?? [])[0]?.value ?? null}
                                    onChange={(_, newValue: number | null) => {
                                        if (newValue === null) return
                                        const option = toggleActivoOptions.find(o => o.value === newValue)
                                        field.onChange(option ? [option] : [])
                                    }}
                                >
                                    {toggleActivoOptions.map(option => (
                                        <ToggleButton key={option.value} value={option.value} color={option.color}>
                                            {option.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}
                        />
                        <Tooltip title='Limpiar filtros'>
                            <IconButton size='small' onClick={handleClearFilters}>
                                <FilterAltOff fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <ItecDataGrid {...cleanDatagridProps} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}

export default EditUserGroups
